import React, { useState, useRef, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Upload, 
  X, 
  FileText, 
  AlertCircle, 
  CheckCircle,
  File,
  Image,
  Video,
  Archive,
  HardDrive,
  Clock,
  Trash2
} from 'lucide-react';

/**
 * BulkUpload - Bulk file upload with progress tracking for materials
 * 
 * Features:
 * - Drag and drop file upload
 * - Multiple file selection
 * - File type validation
 * - File size validation and preview
 * - Upload progress tracking
 * - Version increment type selection
 * - Change notes for version documentation
 */
const BulkUpload = ({ 
  selectedSessions = [], 
  onUploadComplete,
  onUploadProgress,
  maxFileSize = 50 * 1024 * 1024, // 50MB default
  allowedTypes = ['pdf', 'docx', 'pptx', 'xlsx', 'png', 'jpg', 'jpeg', 'gif', 'mp4', 'zip'],
  className = ""
}) => {
  const [files, setFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [changeNotes, setChangeNotes] = useState('');
  const [versionIncrementType, setVersionIncrementType] = useState('patch');
  const [errors, setErrors] = useState([]);
  
  const fileInputRef = useRef(null);

  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    
    switch (extension) {
      case 'pdf':
      case 'doc':
      case 'docx':
        return <FileText className="h-5 w-5 text-red-500" />;
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
        return <Image className="h-5 w-5 text-green-500" />;
      case 'mp4':
      case 'mov':
      case 'avi':
        return <Video className="h-5 w-5 text-blue-500" />;
      case 'zip':
      case 'rar':
      case '7z':
        return <Archive className="h-5 w-5 text-purple-500" />;
      default:
        return <File className="h-5 w-5 text-gray-500" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const validateFile = (file) => {
    const errors = [];
    
    // Check file size
    if (file.size > maxFileSize) {
      errors.push(`File size exceeds ${formatFileSize(maxFileSize)} limit`);
    }
    
    // Check file type
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!allowedTypes.includes(extension)) {
      errors.push(`File type .${extension} is not allowed`);
    }
    
    return errors;
  };

  const handleFiles = useCallback((fileList) => {
    const newFiles = [];
    const newErrors = [];
    
    Array.from(fileList).forEach((file, index) => {
      const fileErrors = validateFile(file);
      
      if (fileErrors.length > 0) {
        newErrors.push({
          id: `error-${Date.now()}-${index}`,
          fileName: file.name,
          errors: fileErrors
        });
      } else {
        // Check for duplicates
        const isDuplicate = files.some(existingFile => 
          existingFile.name === file.name && existingFile.size === file.size
        );
        
        if (!isDuplicate) {
          newFiles.push({
            id: `file-${Date.now()}-${index}`,
            file: file,
            name: file.name,
            size: file.size,
            type: file.type,
            uploaded: false,
            progress: 0
          });
        }
      }
    });
    
    setFiles(prev => [...prev, ...newFiles]);
    setErrors(prev => [...prev, ...newErrors]);
    
    // Clear errors after 5 seconds
    if (newErrors.length > 0) {
      setTimeout(() => {
        setErrors(prev => prev.filter(error => 
          !newErrors.some(newError => newError.id === error.id)
        ));
      }, 5000);
    }
  }, [files, maxFileSize, allowedTypes]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const removeFile = (fileId) => {
    setFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const clearErrors = () => {
    setErrors([]);
  };

  const simulateUpload = async (file) => {
    return new Promise((resolve) => {
      const duration = 2000 + Math.random() * 3000; // 2-5 seconds
      const steps = 20;
      const stepDuration = duration / steps;
      let progress = 0;
      
      const interval = setInterval(() => {
        progress += 100 / steps;
        
        if (progress >= 100) {
          clearInterval(interval);
          setUploadProgress(prev => ({ ...prev, [file.id]: 100 }));
          resolve({
            success: true,
            fileId: `uploaded-${file.id}`,
            cdnUrl: `https://cdn.avenue.ca/materials/${file.name}`,
            size: file.size
          });
        } else {
          setUploadProgress(prev => ({ ...prev, [file.id]: Math.round(progress) }));
        }
      }, stepDuration);
    });
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    if (!changeNotes.trim()) {
      setErrors(prev => [...prev, {
        id: `error-${Date.now()}`,
        fileName: 'Upload Validation',
        errors: ['Change notes are required for version tracking']
      }]);
      return;
    }

    setUploading(true);
    setUploadProgress({});
    
    try {
      // Initialize progress tracking
      const progressData = {};
      files.forEach(file => {
        progressData[file.id] = 0;
      });
      setUploadProgress(progressData);
      
      if (onUploadProgress) {
        onUploadProgress({
          phase: 'uploading',
          filesTotal: files.length,
          filesCompleted: 0,
          currentFile: files[0]?.name
        });
      }

      // Upload files concurrently (simulate)
      const uploadPromises = files.map(async (file) => {
        const result = await simulateUpload(file);
        return { ...file, ...result };
      });

      const uploadResults = await Promise.all(uploadPromises);
      
      // Update file states
      setFiles(prev => prev.map(file => {
        const result = uploadResults.find(r => r.id === file.id);
        return result ? { ...file, uploaded: true, ...result } : file;
      }));

      if (onUploadProgress) {
        onUploadProgress({
          phase: 'processing',
          filesTotal: files.length,
          filesCompleted: files.length,
          message: 'Creating new versions...'
        });
      }

      // Simulate version creation delay
      await new Promise(resolve => setTimeout(resolve, 1500));

      if (onUploadComplete) {
        onUploadComplete({
          files: uploadResults,
          changeNotes,
          versionIncrementType,
          selectedSessions,
          totalFiles: uploadResults.length,
          totalSize: uploadResults.reduce((sum, file) => sum + file.size, 0)
        });
      }

      // Reset form
      setFiles([]);
      setChangeNotes('');
      setUploadProgress({});
      
    } catch (error) {
      console.error('Upload failed:', error);
      setErrors(prev => [...prev, {
        id: `error-${Date.now()}`,
        fileName: 'Upload Error',
        errors: ['Failed to upload files. Please try again.']
      }]);
    } finally {
      setUploading(false);
    }
  };

  const getTotalSize = () => {
    return files.reduce((sum, file) => sum + file.size, 0);
  };

  const getUploadedFilesCount = () => {
    return files.filter(file => file.uploaded).length;
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Upload className="h-5 w-5" />
          <span>Bulk Upload Materials</span>
        </CardTitle>
        <CardDescription>
          Upload materials that will be applied to all selected sessions
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Upload Area */}
        <div
          className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            dragActive 
              ? 'border-blue-400 bg-blue-50' 
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <Upload className="h-8 w-8 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 mb-2">
            Drag and drop files here, or{' '}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              click to browse
            </button>
          </p>
          <p className="text-sm text-gray-500">
            Supported: {allowedTypes.join(', ')} • Max size: {formatFileSize(maxFileSize)}
          </p>
          
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileInput}
            accept={allowedTypes.map(type => `.${type}`).join(',')}
            className="hidden"
          />
        </div>

        {/* Error Messages */}
        {errors.length > 0 && (
          <div className="space-y-2">
            {errors.map((error) => (
              <div key={error.id} className="flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-800">{error.fileName}</p>
                  <ul className="text-sm text-red-600 mt-1">
                    {error.errors.map((err, idx) => (
                      <li key={idx}>• {err}</li>
                    ))}
                  </ul>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearErrors}
                  className="p-1 h-auto text-red-500 hover:text-red-700"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Selected Files */}
        {files.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-900">
                Selected Files ({files.length})
              </h3>
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <HardDrive className="h-4 w-4" />
                <span>Total: {formatFileSize(getTotalSize())}</span>
              </div>
            </div>

            <div className="space-y-2 max-h-64 overflow-y-auto">
              {files.map((file) => (
                <div
                  key={file.id}
                  className="flex items-center space-x-3 p-3 border rounded-lg bg-gray-50"
                >
                  <div className="flex-shrink-0">
                    {file.uploaded ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      getFileIcon(file.name)
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {file.name}
                      </p>
                      <p className="text-xs text-gray-500 ml-2">
                        {formatFileSize(file.size)}
                      </p>
                    </div>
                    
                    {uploading && uploadProgress[file.id] !== undefined && (
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress[file.id]}%` }}
                        />
                      </div>
                    )}
                    
                    {file.uploaded && (
                      <p className="text-xs text-green-600">✓ Uploaded successfully</p>
                    )}
                  </div>

                  {!uploading && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFile(file.id)}
                      className="p-1 h-auto text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upload Configuration */}
        {files.length > 0 && (
          <div className="space-y-4 pt-4 border-t">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="versionType" className="text-sm font-medium">
                  Version Increment Type
                </Label>
                <select
                  id="versionType"
                  value={versionIncrementType}
                  onChange={(e) => setVersionIncrementType(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="patch">Patch (v1.0.1) - Minor updates</option>
                  <option value="minor">Minor (v1.1.0) - New materials added</option>
                  <option value="major">Major (v2.0.0) - Breaking changes</option>
                </select>
              </div>
              
              <div>
                <Label className="text-sm font-medium">
                  Impact Summary
                </Label>
                <div className="mt-1 p-2 bg-blue-50 rounded-md text-sm">
                  <p className="text-blue-800">
                    {selectedSessions.length} sessions • {files.length} files • {formatFileSize(getTotalSize())}
                  </p>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="changeNotes" className="text-sm font-medium">
                Change Notes <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="changeNotes"
                value={changeNotes}
                onChange={(e) => setChangeNotes(e.target.value)}
                placeholder="Describe the changes in this version update..."
                className="mt-1"
                rows={3}
              />
              <p className="text-xs text-gray-500 mt-1">
                Required for version tracking and audit trail
              </p>
            </div>

            <div className="flex items-center justify-between pt-2">
              <div className="text-sm text-gray-600">
                {getUploadedFilesCount() > 0 && (
                  <span className="flex items-center space-x-1 text-green-600">
                    <CheckCircle className="h-4 w-4" />
                    <span>{getUploadedFilesCount()} of {files.length} uploaded</span>
                  </span>
                )}
              </div>
              
              <Button
                onClick={handleUpload}
                disabled={uploading || files.length === 0 || !changeNotes.trim()}
                className="flex items-center space-x-2"
              >
                {uploading ? (
                  <>
                    <Clock className="h-4 w-4 animate-spin" />
                    <span>Uploading...</span>
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    <span>Upload & Create Versions</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BulkUpload;